/**
 * @lines98/client/lib/currency-hud — единый переиспользуемый HUD мета-балансов
 * (золото / осколки / + позже карат).
 *
 * ЗАЧЕМ / WHY THIS EXISTS (owner-directive 2026-05-30 «ствол → ветки»; meta-jeweler-
 * surfaces.md §3.3 «HUD — новый переиспользуемый модуль, single source, НЕ копируется
 * по экранам»). Балансы — расходники прогресса; игрок видит их в шапке КАЖДОЙ
 * мета-поверхности (экран добычи / мастерская / лоток / профиль). Этот модуль —
 * ЕДИНСТВЕННЫЙ объект HUD: монтируется в шапку surface'а, читает `GET /meta/state`
 * (server-truth, ADR-0015 D-3), рендерит иконку валюты + число, умеет «пульс +N»
 * при приросте. В Срезе 1b он живёт на reward-block; рассчитан на переиспользование
 * (мастерская / профиль — будущие срезы монтируют тот же модуль).
 *
 * Server-truth (AC-12): HUD — ПРОЕКЦИЯ. Балансы приходят с сервера (`getMetaState`),
 * клиент не пересчитывает добычу. Reward-block может передать оптимистичный снапшот
 * (`setBalances`) для мгновенного UX, но истина — следующий `refresh()`.
 *
 * SCOPE: золото + осколки (Срез 1b); карат — опц. (`showKarat`, forward-compat,
 * пока всегда 0). NOT IN SCOPE: мутации (faucet — сервер), мастерская/лоток
 * (будущие срезы монтируют HUD как есть).
 *
 * API (как другие lib-модули): `mountCurrencyHud(host, opts) → handle` с
 * `setBalances` / `refresh` / `pulse` / `unmount`. CSS co-located.
 *
 * Cross-references:
 *   - meta-jeweler-surfaces.md §3 (HUD-требования) + §10 (микро-иконки).
 *   - services/meta-api.ts (server-truth read).
 *   - components/meta-icons.ts (плейсхолдер-глифы валют).
 *   - .claude/skills/smooth-motion-animation + ui-performance (анимация только
 *     transform/opacity; prefers-reduced-motion).
 */

import './currency-hud.css';

import { ICON_GOLD, ICON_KARAT, ICON_SHARD } from '../components/meta-icons.js';
import { formatRuNumber } from './format.js';
import { t } from './i18n/index.js';
import { getMetaState, type MetaStateResult } from '../services/meta-api.js';

export interface CurrencyHudOptions {
  /**
   * Показывать карат-счётчик (forward-compat; в Срезе 1b карата в добыче нет, по
   * умолчанию скрыт — лишний chrome на reward-block). Default `false`.
   */
  readonly showKarat?: boolean;
  /**
   * Стартовые балансы (оптимистичный снапшот для мгновенного рендера до прихода
   * `GET /meta/state`). Опц. — без него HUD стартует с «0»+приглушённо (паритет с
   * камнями), пока не загрузит server-truth. Никогда не прочерк.
   */
  readonly initial?: { gold: number; shards: number; karat?: number };
  /**
   * Доп. класс(ы) для позиционирования/контекста surface'а (вид не меняют).
   */
  readonly className?: string;
}

export interface CurrencyHudHandle {
  /** Корневой элемент HUD — потребитель уже получил его смонтированным в host. */
  readonly element: HTMLElement;
  /**
   * Поставить балансы напрямую (оптимистичный снапшот от reward-block). НЕ
   * запрашивает сервер; для server-truth — `refresh()`.
   */
  setBalances(balances: { gold: number; shards: number; karat?: number }): void;
  /**
   * Перечитать `GET /meta/state` (server-truth) и обновить числа. Best-effort:
   * ошибка/гость (401) оставляет текущие числа (HUD не падает). Возвращает
   * результат запроса для потребителя, которому важна ветка.
   */
  refresh(): Promise<MetaStateResult>;
  /**
   * Короткий «пульс +N» у валюты (transform/opacity, §ui-performance). Чисто
   * визуальный акцент прироста; число балансов меняет `setBalances`/`refresh`.
   */
  pulse(currency: 'gold' | 'shards' | 'karat'): void;
  /** Размонтировать: снять DOM + слушатели. */
  unmount(): void;
}

interface CurrencyRow {
  readonly currency: 'gold' | 'shards' | 'karat';
  readonly root: HTMLElement;
  readonly valueEl: HTMLElement;
}

/**
 * Нормализовать возможно-отсутствующий/невалидный баланс к отображаемому числу.
 * Дефолт неизвестного = 0 (НЕ прочерк) — паритет с камнями (gem-pool-hud: `setPool`
 * приводит `pool[X]` к 0 при `undefined`/NaN). Это снимает device-only баг: на 0/null/
 * loading HUD всегда показывает «0»+приглушённо, не «—» (owner playtest 2026-06-11).
 * Чистая функция без сайд-эффектов → юнит-тестируема в node-env (DOM-путь — chromium).
 */
export function currencyDisplayValue(value: number | null | undefined): number {
  return Number.isFinite(value) ? Math.max(0, Math.floor(value as number)) : 0;
}

/**
 * Смонтировать HUD балансов в `host`. Возвращает handle. Если переданы `initial`
 * — рендерит их сразу; иначе «0»+приглушённо (паритет с камнями) до первого `refresh()`.
 */
export function mountCurrencyHud(host: HTMLElement, opts: CurrencyHudOptions = {}): CurrencyHudHandle {
  const root = document.createElement('div');
  root.className = 'currency-hud';
  root.setAttribute('role', 'group');
  root.setAttribute('aria-label', t('game.currency.balancesAria'));
  if (opts.className) root.classList.add(...opts.className.split(/\s+/).filter(Boolean));

  const rows: CurrencyRow[] = [];
  rows.push(buildRow('gold', ICON_GOLD, t('game.currency.gold')));
  rows.push(buildRow('shards', ICON_SHARD, t('game.currency.shards')));
  // 'Карат' пока БЕЗ i18n-ключа в каталоге (forward-compat показ карата, default off):
  // ключа `game.currency.karat` нет в манифесте §1 / ru.json → не выношу (не выдумываю
  // ключ). Когда карат-добыча появится (Срез 2) — ключ заведёт фаза каталога, тогда wire.
  if (opts.showKarat) rows.push(buildRow('karat', ICON_KARAT, 'Карат'));
  for (const r of rows) root.appendChild(r.root);

  host.appendChild(root);

  // Стартовый рендер — ПАРИТЕТ с камнями (gem-pool-hud вызывает setPool(opts.pool ?? [])
  // на mount → нули отрисованы «0»+приглушённо ДО любой гидрации). Дефолт отсутствующего
  // баланса = 0 (как камни), чтобы прочерк «—» не залипал, если значение не успело
  // гидрироваться (owner playtest 2026-06-11: device-only баг на планшете). Снапшот, если
  // есть, перекрывает дефолт; иначе показываем «0»+dim, а не «неизвестно».
  setValue('gold', opts.initial?.gold);
  setValue('shards', opts.initial?.shards);
  if (opts.showKarat) setValue('karat', opts.initial?.karat);

  function rowFor(currency: 'gold' | 'shards' | 'karat'): CurrencyRow | undefined {
    return rows.find((r) => r.currency === currency);
  }

  function setValue(currency: 'gold' | 'shards' | 'karat', value: number | null | undefined): void {
    const row = rowFor(currency);
    if (!row) return;
    // Дефолт неизвестного/невалидного = 0 (паритет с камнями), не прочерк — единый источник
    // `currencyDisplayValue`. Никакого «—» в этом HUD ни при каком значении.
    const n = currencyDisplayValue(value);
    row.valueEl.textContent = formatRuNumber(n);
    // Пустой баланс (0) приглушается — как нулевые камни в хранилище (owner-directive
    // 2026-06-11 «монетку с осколком сделать такими же»): видно, что валюты нет. Дефолт
    // негидрированного значения = 0 (mount зовёт setValue(0)) → «0»+приглушённо без прочерка.
    row.root.classList.toggle('currency-hud-item--empty', n === 0);
  }

  return {
    element: root,

    setBalances(balances): void {
      setValue('gold', balances.gold);
      setValue('shards', balances.shards);
      if (balances.karat !== undefined) setValue('karat', balances.karat);
    },

    async refresh(): Promise<MetaStateResult> {
      const result = await getMetaState();
      if (result.ok) {
        setValue('gold', result.gold);
        setValue('shards', result.shards);
        if (opts.showKarat) setValue('karat', result.karat);
      }
      // На ошибку/гость — текущие числа остаются (HUD не падает, §3.2 default).
      return result;
    },

    pulse(currency): void {
      const row = rowFor(currency);
      if (!row) return;
      // Перезапуск анимации: снять класс → reflow → навесить (иначе повторный
      // вызов не рестартит CSS-анимацию). Только transform/opacity (ui-performance).
      row.root.classList.remove('currency-hud-item--pulse');
      void row.root.offsetWidth; // форс reflow
      row.root.classList.add('currency-hud-item--pulse');
    },

    unmount(): void {
      if (root.parentNode) root.parentNode.removeChild(root);
    },
  };
}

/** Построить одну строку валюты [иконка | число] с aria-меткой. */
function buildRow(
  currency: 'gold' | 'shards' | 'karat',
  glyph: string,
  label: string,
): CurrencyRow {
  const item = document.createElement('div');
  item.className = `currency-hud-item currency-hud-item--${currency}`;

  const icon = document.createElement('span');
  icon.className = 'currency-hud-icon';
  icon.setAttribute('aria-hidden', 'true');
  icon.innerHTML = glyph;

  const value = document.createElement('span');
  value.className = 'currency-hud-value';
  // Дефолт = «0» (НЕ прочерк) — паритет с камнями (gem-pool-hud value стартует с «0»).
  // mount всегда зовёт setValue(...) → «0»+приглушённо до гидрации; никогда «—» в этом
  // HUD (owner playtest 2026-06-11: «должен быть 0 и приглушённое изображение, как на камнях»).
  value.textContent = '0';
  // Доступная подпись валюты — невидимая (визуально несёт иконка), для скринридера.
  value.setAttribute('aria-label', label);

  item.append(icon, value);
  return { currency, root: item, valueEl: value };
}
